import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Card,
  Box,
  BlockStack,
  InlineStack,
  Text,
  Button,
  SkeletonDisplayText,
  SkeletonBodyText,
} from '@shopify/polaris';
import { Redirect } from '@shopify/app-bridge/actions/index.js';
import { fetchWithSessionToken, getAppBridge } from '../lib/appBridge';
import { trackAnalyticsEvent } from '../lib/analytics';

interface OnboardingInfoPageProps {
  shop: string;
  host: string;
}

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'accepted', 'trialing']);

const isSubscriptionActive = (hasSubscription: boolean, status?: string | null) => {
  const normalizedStatus = (status || '').toLowerCase();
  if (ACTIVE_SUBSCRIPTION_STATUSES.has(normalizedStatus)) {
    return true;
  }
  if (normalizedStatus.length > 0) {
    return false;
  }
  return hasSubscription;
};

const OnboardingInfoPage: React.FC<OnboardingInfoPageProps> = ({ shop, host }) => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirectTarget, setRedirectTarget] = useState<string | null>(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [subscriptionName, setSubscriptionName] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [projectOnboardingComplete, setProjectOnboardingComplete] = useState<boolean | null>(null);

  const normalizeShopDomain = (rawShop: string) => {
    const cleaned = rawShop.trim().toLowerCase().replace(/^https?:\/\//, '');
    const withoutQuery = cleaned.split('?')[0];
    const adminStoreMatch = withoutQuery.match(/admin\.shopify\.com\/store\/([^/]+)/);
    if (adminStoreMatch?.[1]) {
      return `${adminStoreMatch[1]}.myshopify.com`;
    }
    const hostOnly = withoutQuery.split('/')[0];
    if (hostOnly.includes('.myshopify.com')) {
      return hostOnly;
    }
    if (!hostOnly.includes('.')) {
      return `${hostOnly}.myshopify.com`;
    }
    return hostOnly;
  };

  const fetchSubscriptionStatus = async (shopDomain: string, hostParam: string) => {
    const params = new URLSearchParams({ shop: shopDomain, host: hostParam });
    const res = await fetchWithSessionToken(hostParam, `/api/subscription-status?${params.toString()}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false as const, data };
    }
    return { ok: true as const, data };
  };

  const fetchNativeStatus = async (shopDomain: string, hostParam: string) => {
    const params = new URLSearchParams({ shop: shopDomain, host: hostParam });
    const res = await fetchWithSessionToken(hostParam, `/api/native-status?${params.toString()}`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false as const, data };
    }
    return { ok: true as const, data };
  };

  const refreshStatus = async (showErrors = true) => {
    if (!shop || !host) return;
    try {
      setIsChecking(true);
      if (showErrors) setError(null);
      const shopDomain = normalizeShopDomain(shop);

      const subscriptionResult = await fetchSubscriptionStatus(shopDomain, host);
      if (subscriptionResult.ok) {
        setHasSubscription(Boolean(subscriptionResult.data?.hasSubscription));
        setSubscriptionStatus(subscriptionResult.data?.subscriptionStatus ?? null);
        setSubscriptionName(subscriptionResult.data?.subscriptionName ?? null);
      }

      const nativeResult = await fetchNativeStatus(shopDomain, host);
      if (!nativeResult.ok) {
        if (showErrors) {
          throw new Error(nativeResult.data?.error || 'Failed to check Shopify connection');
        }
        return;
      }

      const nativeHasSubscription = Boolean(nativeResult.data?.hasSubscription);
      const nativeSubscriptionStatus = nativeResult.data?.subscriptionStatus ?? null;
      const nativeSubscriptionName = nativeResult.data?.subscriptionName ?? null;
      const nativeOnboardingComplete = nativeResult.data?.projectOnboardingComplete ?? null;
      const nativeConnected = Boolean(nativeResult.data?.connected);

      setConnected(nativeConnected);
      setProjectOnboardingComplete(nativeOnboardingComplete);
      setHasSubscription(nativeHasSubscription || subscriptionResult.data?.hasSubscription);
      setSubscriptionStatus(nativeSubscriptionStatus || subscriptionResult.data?.subscriptionStatus || null);
      setSubscriptionName(nativeSubscriptionName || subscriptionResult.data?.subscriptionName || null);

      const resolvedHasSubscription =
        nativeHasSubscription || Boolean(subscriptionResult.data?.hasSubscription);
      const resolvedStatus =
        nativeSubscriptionStatus || subscriptionResult.data?.subscriptionStatus || null;
      const subscriptionActive = isSubscriptionActive(resolvedHasSubscription, resolvedStatus);

      if (!subscriptionActive) {
        setRedirectTarget('/pricing');
        return;
      }

      if (nativeOnboardingComplete === true) {
        setRedirectTarget('/');
        return;
      }
    } catch (err: any) {
      if (showErrors) {
        setError(err?.message || 'Failed to check Shopify connection');
      }
    } finally {
      setIsChecking(false);
      setInitialLoad(false);
    }
  };

  useEffect(() => {
    if (!shop || !host) return;
    trackAnalyticsEvent('onboarding_step_viewed', {
      route: '/onboarding-info',
      shopDomain: normalizeShopDomain(shop),
      stage: 'INFO',
    });
    refreshStatus(false);
  }, [shop, host]);

  useEffect(() => {
    if (!redirectTarget) return;
    navigate(redirectTarget, { replace: true });
  }, [redirectTarget, navigate]);

  const redirectRemote = (url: string) => {
    const app = getAppBridge(host);
    if (app) {
      const redirect = Redirect.create(app);
      redirect.dispatch(Redirect.Action.REMOTE, { url, newContext: false });
      return;
    }
    window.top?.location?.assign(url);
  };

  const openGenseoOnboarding = () => {
    if (!shop) return;
    const shopDomain = normalizeShopDomain(shop);
    trackAnalyticsEvent('genseo_connection_started', {
      route: '/onboarding-info',
      shopDomain,
      destination: 'genseo_onboarding',
    });
    trackAnalyticsEvent('external_dashboard_docs_links_opened', {
      route: '/onboarding-info',
      shopDomain,
      destination: 'genseo_onboarding',
    });
    const params = new URLSearchParams({
      source: 'shopify',
      shop: shopDomain,
      shopDomain,
    });
    if (host) {
      params.set('host', host);
    }
    const onboardingPath = `/shopify/connect?${params.toString()}`;
    const loginUrl = new URL('https://app.genseo.co/login');
    loginUrl.searchParams.set('redirect', onboardingPath);
    redirectRemote(loginUrl.toString());
  };


  const showSkeleton = initialLoad || isChecking || Boolean(redirectTarget);
  const statusSummary = [
    connected ? 'Shopify connected' : 'Shopify not connected',
    isSubscriptionActive(hasSubscription, subscriptionStatus)
      ? 'Subscription active'
      : 'Subscription inactive',
    subscriptionName ? `Plan: ${subscriptionName}` : null,
    projectOnboardingComplete === true
      ? 'Onboarding complete'
      : projectOnboardingComplete === false
      ? 'Onboarding pending'
      : null,
  ]
    .filter(Boolean)
    .join(' • ');

  if (showSkeleton) {
    return (
      <Page>
        <Card>
          <div style={{ marginTop: '1rem' }}>
            <Box padding="800">
            <BlockStack gap="400">
              <SkeletonDisplayText size="large" />
              <SkeletonBodyText lines={4} />
              <SkeletonBodyText lines={2} />
            </BlockStack>
            </Box>
          </div>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <BlockStack gap="400">
        <div style={{ maxWidth: '480px', margin: '2rem auto 0' }}>
          <Card>
            <Box padding="600">
              <BlockStack gap="400">
                <BlockStack gap="200">
                  <Text variant="headingLg" as="h1" alignment="center">
                    Complete your Genseo onboarding
                  </Text>
                  <Text as="p" tone="subdued" alignment="center">
                    Finish onboarding in Genseo, connect your Shopify store, then return here to continue.
                  </Text>
                </BlockStack>

                <BlockStack gap="200">
                  <InlineStack align="center" gap="200">
                    <Button variant="primary" onClick={openGenseoOnboarding}>
                      Continue with onboarding
                    </Button>
                  </InlineStack>
                </BlockStack>
                {statusSummary && (
                  <Text as="p" tone="subdued" variant="bodySm" alignment="center">
                    {statusSummary}
                  </Text>
                )}

                {error && (
                  <Text as="p" tone="critical" variant="bodySm" alignment="center">
                    {error}
                  </Text>
                )}
              </BlockStack>
            </Box>
          </Card>
        </div>
      </BlockStack>
    </Page>
  );
};

export default OnboardingInfoPage;
